import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, Pause, Play, Music, Headphones, Check } from 'lucide-react';
import { createMelody, SONGS } from '../utils/cleanMelody';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/chores/pep`;
const STREAK_API = `${process.env.REACT_APP_BACKEND_URL}/api/chores`;
export const STREAK_EVENT = 'focus-critter:clean-streak';
const MINUTES = [5, 10, 15];
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

function SongPicker({ song, setSong }) {
  const prev = useRef(null);
  useEffect(() => () => { prev.current?.close(); prev.current = null; }, []);
  const pick = (id) => {
    setSong(id);
    localStorage.setItem('fc-clean-song', id);
    prev.current?.close();
    const m = createMelody(id);
    prev.current = m;
    m.play();
    setTimeout(() => { if (prev.current === m) { m.close(); prev.current = null; } }, 3500);
  };
  return (
    <div className="grid grid-cols-2 gap-1.5" data-testid="clean-timer-songs">
      {Object.entries(SONGS).map(([id, s]) => (
        <button key={id} onClick={() => pick(id)} data-testid={`clean-timer-song-${id}`}
          className={`neo-btn py-1.5 px-2 text-sm font-bold text-left ${song === id ? 'bg-[#FFF0B3]' : 'bg-white'}`}>{s.emoji} {s.label}</button>
      ))}
      <p className="col-span-2 text-[11px] opacity-60">Tryck för att lyssna en snutt.</p>
    </div>
  );
}

function Setup({ chore, minutes, setMinutes, music, setMusic, pep, setPep, song, setSong, onStart }) {
  return (
    <div className="space-y-4" data-testid="clean-timer-setup">
      <p className="text-sm">Vi tar <b>{chore.text}</b> tillsammans. Hur länge orkar du idag?</p>
      <div className="flex gap-2">
        {MINUTES.map((m) => (
          <button key={m} onClick={() => setMinutes(m)} data-testid={`clean-timer-min-${m}`}
            className={`flex-1 neo-btn py-2 font-black ${minutes === m ? 'bg-[#FFD6E8]' : 'bg-white'}`}>{m} min</button>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={() => setMusic('melody')} data-testid="clean-timer-music-melody"
          className={`flex-1 neo-btn py-2 text-sm font-bold flex items-center justify-center gap-1 ${music === 'melody' ? 'bg-[#CDEBFF]' : 'bg-white'}`}><Music className="w-4 h-4" /> Glad melodi</button>
        <button onClick={() => setMusic('own')} data-testid="clean-timer-music-own"
          className={`flex-1 neo-btn py-2 text-sm font-bold flex items-center justify-center gap-1 ${music === 'own' ? 'bg-[#CDEBFF]' : 'bg-white'}`}><Headphones className="w-4 h-4" /> Egen musik</button>
      </div>
      {music === 'melody' && <SongPicker song={song} setSong={setSong} />}
      {music === 'own' && <p className="text-xs opacity-70" data-testid="clean-timer-own-hint">Starta din egen låt, till exempel på Spotify. Jag plingar bara när vi börjar, halvvägs och när tiden är slut.</p>}
      <label className="flex items-center gap-2 text-sm font-bold">
        <input type="checkbox" checked={pep} onChange={(e) => setPep(e.target.checked)} data-testid="clean-timer-pep" /> Elsa peppar under tiden
      </label>
      <button onClick={onStart} className="w-full neo-btn py-3 bg-[#C2306B] text-white font-black text-lg" data-testid="clean-timer-start">Nu kör vi! 🧽</button>
    </div>
  );
}

function Running({ left, total, paused, onPause, onFinish }) {
  const pct = 1 - left / total;
  return (
    <div className="text-center space-y-4" data-testid="clean-timer-running">
      <div className="relative w-48 h-48 mx-auto">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r="44" fill="#FFF6FA" stroke="#FFD6E8" strokeWidth="8" />
          <circle cx="50" cy="50" r="44" fill="none" stroke="#C2306B" strokeWidth="8" strokeLinecap="round"
            strokeDasharray={276.5} strokeDashoffset={276.5 * (1 - pct)} style={{ transition: 'stroke-dashoffset 1s linear' }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-4xl font-black font-['Fredoka'] tabular-nums" data-testid="clean-timer-left">{fmt(left)}</span>
          <span className={`text-3xl ${paused ? '' : 'animate-bounce'}`}>🧽</span>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={onPause} className="flex-1 neo-btn py-2 bg-white font-bold flex items-center justify-center gap-1" data-testid="clean-timer-pause">
          {paused ? <><Play className="w-4 h-4" /> Fortsätt</> : <><Pause className="w-4 h-4" /> Paus</>}
        </button>
        <button onClick={onFinish} className="flex-1 neo-btn py-2 bg-[#C9F2DC] font-bold flex items-center justify-center gap-1" data-testid="clean-timer-finish">
          <Check className="w-4 h-4" /> Klar nu!
        </button>
      </div>
    </div>
  );
}

export default function CleanTimer({ chore, onDone, onClose }) {
  const [minutes, setMinutes] = useState(10);
  const [music, setMusic] = useState('melody');
  const [pep, setPep] = useState(true);
  const [phase, setPhase] = useState('setup');
  const [left, setLeft] = useState(0);
  const [paused, setPaused] = useState(false);
  const [song, setSong] = useState(() => (SONGS[localStorage.getItem('fc-clean-song')] ? localStorage.getItem('fc-clean-song') : 'disco'));
  const [streak, setStreak] = useState(null);
  const r = useRef({ melody: null, clips: {}, said: {}, endAt: 0, audio: null });

  const say = (kind) => {
    const R = r.current;
    if (R.said[kind]) return;
    R.said[kind] = true;
    R.melody?.pling();
    const url = pep && R.clips[kind];
    if (!url) return;
    R.melody?.duck(true);
    R.audio?.pause();
    R.audio = new Audio(url);
    R.audio.onended = () => R.melody?.duck(false);
    R.audio.play().catch(() => R.melody?.duck(false));
  };

  const finish = (completed) => {
    say('done');
    r.current.melody?.pause();
    setPhase('done');
    onDone(chore);
    axios.post(`${STREAK_API}/timer-session`, { minutes, chore_id: chore.id, completed }, { withCredentials: true })
      .then((res) => { setStreak(res.data); window.dispatchEvent(new CustomEvent(STREAK_EVENT, { detail: res.data })); })
      .catch(() => {});
  };

  const start = async () => {
    const R = r.current;
    R.melody = createMelody(song);
    if (music === 'melody') R.melody.play();
    R.endAt = Date.now() + minutes * 60000;
    setLeft(minutes * 60);
    setPhase('run');
    if (pep) {
      await Promise.all(['start', 'half', 'last', 'done'].map((kind) =>
        axios.post(API, { kind, chore: chore.text }, { withCredentials: true, responseType: 'blob' })
          .then((res) => { R.clips[kind] = URL.createObjectURL(res.data); }).catch(() => {})));
    }
    say('start');
  };

  useEffect(() => {
    if (phase !== 'run' || paused) return undefined;
    const t = setInterval(() => {
      const s = Math.max(0, Math.round((r.current.endAt - Date.now()) / 1000));
      setLeft(s);
      const total = minutes * 60;
      if (s <= total / 2) say('half');
      if (s <= 60 && total > 180) say('last');
      if (s === 0) { clearInterval(t); finish(true); }
    }, 500);
    return () => clearInterval(t);
  });

  const togglePause = () => {
    const R = r.current;
    if (paused) {
      R.endAt = Date.now() + left * 1000;
      if (music === 'melody') R.melody?.play();
    } else R.melody?.pause();
    setPaused(!paused);
  };

  useEffect(() => {
    const R = r.current;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      R.audio?.pause();
      R.melody?.close();
      Object.values(R.clips).forEach((u) => URL.revokeObjectURL(u));
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[400] bg-black/40 flex items-center justify-center p-3" onClick={onClose} data-testid="clean-timer">
      <div className="w-full max-w-sm neo-card bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-black font-['Fredoka'] text-xl">Städa med Elsa 👵🏼</h3>
          <button onClick={onClose} aria-label="Stäng" data-testid="clean-timer-close"><X className="w-5 h-5" /></button>
        </div>
        {phase === 'setup' && <Setup {...{ chore, minutes, setMinutes, music, setMusic, pep, setPep, song, setSong }} onStart={start} />}
        {phase === 'run' && <Running left={left} total={minutes * 60} paused={paused} onPause={togglePause} onFinish={() => finish(false)} />}
        {phase === 'done' && (
          <div className="text-center space-y-3" data-testid="clean-timer-done">
            <p className="text-5xl">🎉</p>
            <p className="font-black">{chore.text} är avbockad!</p>
            <p className="text-sm">Farmor är så stolt över dig. Ta en liten paus nu, med något gott 💛</p>
            {streak && (
              <div className="rounded-xl bg-[#FFF0B3] px-3 py-2 text-sm font-bold" data-testid="clean-timer-streak">
                🔥 {streak.streak} {streak.streak === 1 ? 'dag' : 'dagar'} i rad med Elsa-städning
                {streak.new_badge && <p className="text-lg mt-1 animate-bounce" data-testid="clean-timer-new-badge">{streak.new_badge.emoji} {streak.new_badge.text}</p>}
                {!streak.new_badge && streak.next && <p className="text-xs font-normal mt-0.5">{streak.next - streak.streak} till nästa märke</p>}
              </div>
            )}
            <button onClick={onClose} className="neo-btn px-5 py-2 bg-[#C9F2DC] font-black" data-testid="clean-timer-done-close">Tack, Elsa!</button>
          </div>
        )}
      </div>
    </div>
  );
}
